import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/Alert';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';

const UserManagement = () => {
    const { t } = useTranslation();
    const { user, isAdmin } = useAuth();
    const [organization, setOrganization] = useState(null);
    const [userRole, setUserRole] = useState('user');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    // Invitation form
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('user');
    const [inviteSubscription, setInviteSubscription] = useState('BASIC');
    const [inviteBillingPeriod, setInviteBillingPeriod] = useState('monthly');
    const [inviting, setInviting] = useState(false);

    useEffect(() => {
        fetchOrganization();
    }, []);

    const fetchOrganization = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/organizations/my-organization');
            setOrganization(response.data.organization);
            setUserRole(response.data.userRole);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleInviteUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            setInviting(true);
            await axios.post('/api/organizations/invite', {
                email: inviteEmail,
                role: inviteRole,
                subscription: inviteSubscription,
                billingPeriod: inviteBillingPeriod,
            });

            setSuccess(t('organizationSettings.success.invitationSent', { email: inviteEmail }));
            setInviteEmail('');
            setInviteRole('user');
            setInviteSubscription('BASIC');
            setInviteBillingPeriod('monthly');

            // Refresh organization data
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.inviteFailed'));
        } finally {
            setInviting(false);
        }
    };

    const handleChangeRole = async (userId, newRole) => {
        setError('');
        setSuccess('');

        try {
            await axios.put(`/api/organizations/members/${userId}/role`, {
                role: newRole,
            });

            setSuccess(t('organizationSettings.success.roleUpdated'));
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.roleUpdateFailed'));
        }
    };

    const handleRemoveMember = async (userId, memberName) => {
        const proceed = await new Promise((resolve) => {
            setConfirmDialog({
                isOpen: true,
                title: t('confirmDelete.title'),
                message: t('confirmDelete.member', { name: memberName }),
                onConfirm: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(false);
                }
            });
        });

        if (!proceed) return;

        setError('');
        setSuccess('');

        try {
            await axios.delete(`/api/organizations/members/${userId}`);
            setSuccess(t('organizationSettings.success.memberRemoved'));
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.removeMemberFailed'));
        }
    };

    const handleCancelInvitation = async (invitationId) => {
        setError('');
        setSuccess('');

        try {
            await axios.delete(`/api/organizations/invitations/${invitationId}`);
            setSuccess(t('organizationSettings.success.invitationCancelled'));
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.cancelInvitationFailed'));
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <LoadingSpinner size="lg" text={t('organizationSettings.loading')} />
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="card text-center">
                    <p className="text-gray-600">{t('organizationSettings.noOrganization')}</p>
                </div>
            </div>
        );
    }

    const isUserAdmin = isAdmin();

    return (
        <div className="page-transition container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">{t('organizationSettings.tabs.users')}</h1>
                <p className="text-gray-600">
                    {t('userManagement.subtitleOrg', { name: organization.name })}
                </p>
            </div>

            {/* Alerts */}
            <Alert type="error" message={error} onDismiss={() => setError('')} />
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Invite User - Admin Only */}
                {isUserAdmin && (
                    <div className="lg:col-span-1">
                        <div className="card">
                            <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.inviteTitle')}</h2>
                            <form onSubmit={handleInviteUser} className="space-y-4">
                                <div>
                                    <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.users.emailLabel')}
                                    </label>
                                    <input
                                        type="email"
                                        id="inviteEmail"
                                        required
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        className="input-field"
                                        placeholder={t('userManagement.emailPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="inviteSubscription" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.users.subscriptionPlan')}
                                    </label>
                                    <select
                                        id="inviteSubscription"
                                        value={inviteRole === 'admin' ? 'admin' : inviteSubscription}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === 'admin') {
                                                setInviteRole('admin');
                                                setInviteSubscription(user?.subscription || 'BASIC');
                                            } else {
                                                setInviteSubscription(val);
                                                setInviteRole(val === 'PRO' ? 'proUser' : 'user');
                                            }
                                        }}
                                        className="input-field"
                                    >
                                        <option value="BASIC">{t('userManagement.planBasic')}</option>
                                        <option value="PRO">{t('userManagement.planPro')}</option>
                                        <option value="admin">{t('userManagement.planAdmin')}</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="inviteBillingPeriod" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('userManagement.billingPeriodLabel')}
                                    </label>
                                    <select
                                        id="inviteBillingPeriod"
                                        value={inviteBillingPeriod}
                                        onChange={(e) => setInviteBillingPeriod(e.target.value)}
                                        className="input-field"
                                    >
                                        <option value="monthly">{t('profile.subscription.monthly')}</option>
                                        <option value="yearly">{t('userManagement.yearlySavings')}</option>
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    disabled={inviting}
                                    className="btn-primary w-full disabled:opacity-50"
                                >
                                    {inviting ? t('organizationSettings.users.sending') : t('organizationSettings.users.sendInvitation')}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Members List */}
                <div className={isUserAdmin ? 'lg:col-span-2' : 'lg:col-span-3'}>
                    <div className="card">
                        <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.membersTitle')}</h2>

                        <div className="space-y-3">
                            {organization.members.map((member) => (
                                <div
                                    key={member._id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-blue-600 font-semibold">
                                                {member.user.firstName[0]}{member.user.lastName[0]}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-semibold">
                                                {member.user.firstName} {member.user.lastName}
                                                {organization.owner._id === member.user._id && (
                                                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                                        {t('organizationSettings.users.owner')}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-sm text-gray-600">{member.user.email}</p>
                                            <p className="text-xs text-gray-500">
                                                {t('organizationSettings.users.joined', { date: new Date(member.joinedAt).toLocaleDateString() })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        {isUserAdmin && organization.owner._id !== member.user._id ? (
                                            <>
                                                <select
                                                    value={member.role}
                                                    onChange={(e) => handleChangeRole(member.user._id, e.target.value)}
                                                    className="input-field text-sm"
                                                >
                                                    <option value="user">{t('organizationSettings.users.roleUserShort')}</option>
                                                    <option value="proUser">{t('organizationSettings.users.roleProUserShort')}</option>
                                                    <option value="admin">{t('organizationSettings.users.roleAdminShort')}</option>
                                                </select>
                                                <button
                                                    onClick={() => handleRemoveMember(member.user._id, `${member.user.firstName} ${member.user.lastName}`)}
                                                    className="text-red-600 hover:text-red-700 p-2"
                                                    title={t('organizationSettings.users.removeMember')}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </>
                                        ) : (
                                            <span className="text-sm bg-gray-200 px-3 py-1 rounded capitalize">
                                                {member.role === 'user' ? t('organizationSettings.users.roleUserShort') : member.role === 'proUser' ? t('organizationSettings.users.roleProUserShort') : member.role === 'admin' ? t('organizationSettings.users.roleAdminShort') : member.role}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pending Invitations */}
                    {isUserAdmin && organization.invitations && organization.invitations.length > 0 && (
                        <div className="card mt-6">
                            <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.pendingTitle')}</h2>

                            <div className="space-y-3">
                                {organization.invitations
                                    .filter(inv => new Date(inv.expiresAt) > new Date())
                                    .map((invitation) => (
                                        <div
                                            key={invitation._id}
                                            className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                                        >
                                            <div>
                                                <p className="font-semibold">{invitation.email}</p>
                                                <p className="text-sm text-gray-600">
                                                    {t('userManagement.planLabel')}: <span className="font-medium">{invitation.subscription || 'BASIC'}</span>
                                                    {invitation.billingPeriod && (
                                                        <span className="text-gray-500"> · {invitation.billingPeriod}</span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500 capitalize">{t('organizationSettings.users.role')} {invitation.role}</p>
                                                <p className="text-xs text-gray-500">
                                                    {t('organizationSettings.users.expires', { date: new Date(invitation.expiresAt).toLocaleDateString() })}
                                                </p>
                                                {invitation.invitedBy && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('organizationSettings.users.invitedBy', { name: `${invitation.invitedBy.firstName} ${invitation.invitedBy.lastName}` })}
                                                    </p>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => handleCancelInvitation(invitation._id)}
                                                className="text-red-600 hover:text-red-700 text-sm font-semibold"
                                            >
                                                {t('organizationSettings.users.cancelInvitation')}
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
            />
        </div>
    );
};

export default UserManagement;
